
import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Button,
  Stack,
} from "@mui/material";
import { GridFilterListIcon } from "@mui/x-data-grid";

interface FilterMenuProps {
  topics: any[];
  subtopics: any[];
  clusters: any[];
  localFilterState: {
    topic: string;
    subtopic: string;
    cluster: string;
  };
  setLocalFilterState: React.Dispatch<
    React.SetStateAction<{
      topic: string;
      subtopic: string;
      cluster: string;
    }>
  >;
}

const FilterMenu: React.FC<FilterMenuProps> = ({
  topics,
  subtopics,
  clusters,
  localFilterState,
  setLocalFilterState,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tempState, setTempState] = useState(localFilterState);

  useEffect(() => {
    if (!anchorEl) {
      // reset temp state when menu is closed without applying
      setTempState(localFilterState);
    }
  }, [anchorEl, localFilterState]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelect = (
    level: "topic" | "subtopic" | "cluster",
    value: string
  ) => {
    setTempState((prev) => ({
      ...prev,
      [level]: value,
    }));
  };

  const handleApply = () => {
    setLocalFilterState(tempState);
    setAnchorEl(null);
  };

  const handleCancel = () => {
    setTempState(localFilterState);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const getCounts = (level: "topic" | "subtopic" | "cluster", type: string) => {
    let data: any[] = [];
    if (level === "topic") data = topics;
    if (level === "subtopic") data = subtopics;
    if (level === "cluster") data = clusters;

    switch (type) {
      case "all":
        return data.length;
      case "mapped":
        return data.filter(
          (d) => d.pending + d.reviewed + d.save_for_later !== 0
        ).length;
      case "pending":
        return data.filter((d) => d.pending + d.save_for_later > 0).length;
      default:
        return 0;
    }
  };

  return (
    <Box>
      <IconButton
        onClick={handleClick}
        color="primary"
        sx={{ p: "2px", borderRadius: "4px" }}
      >
        <Badge
          color="primary"
          variant="dot"
          invisible={
            localFilterState.topic === "all" &&
            localFilterState.subtopic === "all" &&
            localFilterState.cluster === "all"
          }
        >
          <GridFilterListIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCancel}
        PaperProps={{ sx: { minWidth: 200, p: "5px" } }}
      >
        {/* Topic Filters */}
        <Typography variant="h6" sx={{ px: "5px", pt: "5px",fontSize:"0.9rem",fontWeight:600 }}>
          Topic
        </Typography>
        {["all", "mapped", "pending"].map((option) => (
          <MenuItem
            key={`topic-${option}`}
            selected={tempState.topic === option}
            onClick={() => handleSelect("topic", option)}
          >
            {option.charAt(0).toUpperCase() + option.slice(1)} (
            {getCounts("topic", option)})
          </MenuItem>
        ))}
        <Divider />

        {/* Subtopic Filters */}
        <Typography variant="h6" sx={{ px: "5px", pt: "5px",fontSize:"0.9rem",fontWeight:600 }}>
          Subtopic
        </Typography>
        {["all", "mapped", "pending"].map((option) => (
          <MenuItem
            key={`subtopic-${option}`}
            selected={tempState.subtopic === option}
            onClick={() => handleSelect("subtopic", option)}
          >
            {option.charAt(0).toUpperCase() + option.slice(1)} (
            {getCounts("subtopic", option)})
          </MenuItem>
        ))}
        <Divider />

        {/* Cluster Filters */}
        <Typography variant="h6" sx={{ px: "5px", pt: "5px",fontSize:"0.9rem",fontWeight:600 }}>
          Cluster
        </Typography>
        {["all", "mapped", "pending"].map((option) => (
          <MenuItem
            key={`cluster-${option}`}
            selected={tempState.cluster === option}
            onClick={() => handleSelect("cluster", option)}
          >
            {option.charAt(0).toUpperCase() + option.slice(1)} (
            {getCounts("cluster", option)})
          </MenuItem>
        ))}

        {/* Actions */}
        <Divider sx={{ my: 1 }} />
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          px={2}
          pb={1}
        >
          <Button size="small" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleApply}>
            Apply
          </Button>
        </Stack>
      </Menu>
    </Box>
  );
};

export default FilterMenu;
